import * as AppUrls from "constants/AppUrls";
import DispatchContext from "context/DispatchContext";
import StateContext from "context/StateContext";
import { checkIfAllowed, notificationMessage } from "global/helpers";
import { useContext, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useHistory, useLocation } from "react-router-dom";
import backArrow from "img/back-arrow.svg";
import ConfirmationModal from "components/ConfirmationModal/ConfirmationModal";
import { ErrorResponse } from "components/data/Errors.model";
import {
  CREATE_SUCCESS_MSG,
  UPDATE_SUCCESS_MSG,
} from "constants/NotificationMsgs";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { ItemClass } from "./InventorySetup.model";
import {
  createItemClass,
  deleteItemClass,
  editItemClass,
  getItemClasses,
} from "./InventorySetup.services";

const NewItemClasses = ({ match, props }) => {
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<number>(null);
  const appDispatch = useContext(DispatchContext);
  const appState = useContext(StateContext);
  const queryClient = useQueryClient();
  const { error: errorLoadingData } = useQuery<any, ErrorResponse>(
    "get_classes",
    getItemClasses
  );

  const location = useLocation();
  useEffect(() => {
    if (location.state) {
      const { id, name, description } = location.state;
      setSelectedId(id);
      setValue("name", name);
      setValue("description", description);
    }
  }, [location.state]);

  const formMethods = useForm({ mode: "onChange", reValidateMode: "onChange" });
  const {
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { isDirty, errors },
  } = formMethods;

  useEffect(() => {
    if (errorLoadingData?.errorMessage) {
      let notification = {
        variant: "danger",
        msg: errorLoadingData.errorMessage,
      };
      appDispatch({ type: "notification", value: notification });
    }
  }, [errorLoadingData, appDispatch]);

  const addClass = useMutation(createItemClass, {
    async onSuccess() {
      queryClient.invalidateQueries("get_classes");
      let notification = {
        variant: "success",
        msg: `Item class ${CREATE_SUCCESS_MSG}`,
      };
      appDispatch({ type: "notification", value: notification });
      history.push(AppUrls.item_class);
    },
    onError(error) {
      let notification = {
        variant: "danger",
        msg: notificationMessage(error, "problem adding item class"),
      };
      appDispatch({ type: "notification", value: notification });
    },
  });

  const updateClass = useMutation(editItemClass, {
    async onSuccess() {
      queryClient.invalidateQueries("get_classes");
      let notification = {
        variant: "success",
        msg: `Information ${UPDATE_SUCCESS_MSG}`,
      };
      appDispatch({ type: "notification", value: notification });
      history.push(AppUrls.item_class);
    },
    onError(error) {
      let notification = {
        variant: "danger",
        msg: notificationMessage(error, "problem editing item class"),
      };
      appDispatch({ type: "notification", value: notification });
    },
  });

  const removeItem = useMutation(deleteItemClass, {
    async onSuccess() {
      queryClient.invalidateQueries("get_classes");
      let notification = {
        variant: "success",
        msg: "Information deleted successfully",
      };
      appDispatch({ type: "notification", value: notification });
    },
    onError(error) {
      let notification = {
        variant: "danger",
        msg: notificationMessage(error, "problem removing item class"),
      };
      appDispatch({ type: "notification", value: notification });
    },
  });

  const handleDeleteItem = (itemId) => {
    removeItem.mutate(itemId);
    setSelectedId(null);
    setShowDeleteModal(false);
  };

  const history = useHistory();

  const onSubmit = (values) => {
    const data: ItemClass = {
      itemClassId: selectedId ? selectedId : 0,
      className: values.name,
      description: values.description,
    };
    if (!selectedId) {
      addClass.mutate(data);
    } else {
      updateClass.mutate(data);
    }
  };

  const resetForm = () => {
    reset({ name: "", description: "" });
  };

  useEffect(() => {
    appDispatch({
      type: "loading",
      value:
        addClass.isLoading || updateClass.isLoading || removeItem.isLoading,
    });
  }, [
    addClass.isLoading,
    updateClass.isLoading,
    removeItem.isLoading,
    appDispatch,
  ]);

  return (
    <>
      <div className="static-page">
        <FormProvider {...formMethods}>
          <form id="new-method-form" onSubmit={handleSubmit(onSubmit)}>
            <div className="  page-title page-title-editable">
              <div className="d-flex flex-row">
                <div className="back-btn">
                  <img
                    src={backArrow}
                    alt="back arrow"
                    onClick={() => history.push(AppUrls.item_class)}
                  />
                </div>
                <div>
                  <p className="">Item Class</p>
                </div>
              </div>
              <div className="d-flex" style={{ marginLeft: "10px" }}>
                <button
                  type="button"
                  className="btn btn-outline-primary custom-outline custom-outline"
                  onClick={resetForm}
                >
                  Reset
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!isDirty}
                >
                  {!selectedId ? "Add Class" : "Save Changes"}
                </button>
              </div>
            </div>
            <div className="page-content-wrapper">
              <div>
                {(checkIfAllowed(appState.allowedUrls, "add", match.path) ||
                  checkIfAllowed(appState.allowedUrls, "edit", match.path)) && (
                  <div className="row m-3">
                    <div className="col-4">
                      <Controller
                        control={control}
                        name="name"
                        render={({ field }) => (
                          <>
                            <label>
                              Product Line{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              {...field}
                              type={"text"}
                              className={`form-control ${
                                errors.name ? "required_field" : ""
                              } `}
                              required={true}
                            />
                          </>
                        )}
                      />
                      {errors.name && errors.name.type === "maxLength" && (
                        <p className="text-danger">
                          Max length for name value must not exceed 15
                          characters.
                        </p>
                      )}
                      {errors.name && errors.name.type === "required" && (
                        <p className="text-danger">This field is required</p>
                      )}
                    </div>
                    <div className="col-4">
                      <label>
                        Description <span className="text-danger">*</span>
                      </label>
                      <Controller
                        control={control}
                        name="description"
                        render={({ field }) => (
                          <>
                            <input
                              {...field}
                              className={
                                errors.description
                                  ? "form-control required_field"
                                  : "form-control"
                              }
                              required={true}
                            />
                            {errors.description &&
                              errors.description.type === "required" && (
                                <p className="text-danger">
                                  This field is required
                                </p>
                              )}
                          </>
                        )}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
      <ConfirmationModal
        title="Delete Class"
        message="Are you sure you want to delete the item?"
        showModal={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => handleDeleteItem(selectedId)}
        cancelBtnTitle="Keep"
        confirmBtnTitle="Delete"
        type="confirmation-danger"
      />
    </>
  );
};

export default NewItemClasses;
